import Vue from 'vue';
import Vuex from 'vuex';
import axios from 'axios';
import AuthApi from '@shared/auth/AuthApi';
import {
  createOnboardingStoreParts,
} from '@/store/onboarding-store';
import { startBusinessGuide } from '@/lib/BusinessGuide';
import { startBillingGuide } from '@/lib/BillingGuide';
import { startSingleSubscriptionGuide } from '@/lib/SingleSubscriptionGuide';
import { startApartmentsGuide } from '@/lib/ApartmentsGuide';
import { startUserInfoGuide } from '@/lib/UserInfoGuide';

import i18n from '../i18n';
// import router from '../router';

Vue.use(Vuex);

axios.defaults.baseURL = `${process.env.VUE_APP_APP_URL}/api`;
axios.defaults.withCredentials = true;

const onboardingStoreParts = createOnboardingStoreParts({
  axios,
  startBusinessGuide,
  startBillingGuide,
  startSingleSubscriptionGuide,
  startApartmentsGuide,
  startUserInfoGuide,
});

export default new Vuex.Store({
  state: {
    user: null,
    authChecked: false,
    messages: [],
    errorsBackend: {},
    loading: false,
    backgroundColor: 'transparent',
    calculator: {},
    editReservationId: null,
    deleteReservation: null,
    reservationPreview: null,
    hotelReservationPreview: null,
    inquiryPreview: null,
    placePreview: null,

    showReservationModal: false,
    appDownloadLinks: {
      android: 'https://play.google.com/store/apps/details?id=com.bookaweb.app',
      ios: 'https://apps.apple.com/app/id6476718572',
    },
    ...onboardingStoreParts.state,
  },

  mutations: {
    updateUser(state, user) {
      state.user = user;
    },
    setAuthChecked(state, checked) {
      state.authChecked = checked;
    },
    setPriceCalculator(state, calculator) {
      state.calculator = calculator;
    },
    editReservationId(state, id) {
      state.editReservationId = id;
    },
    deleteReservation(state, id) {
      state.deleteReservation = id;
    },
    clearDeleteReservation(state) {
      state.deleteReservation = undefined;
    },

    setReservationPreview(state, reservation) {
      state.reservationPreview = reservation;
    },
    setHotelReservationPreview(state, reservation) {
      state.hotelReservationPreview = reservation;
    },
    setInquiryPreview(state, inquiry) {
      state.inquiryPreview = inquiry;
    },
    clearReservationPreview(state) {
      state.reservationPreview = undefined;
    },
    clearHotelReservationPreview(state) {
      state.hotelReservationPreview = undefined;
    },
    clearInquiryPreview(state) {
      state.inquiryPreview = undefined;
    },
    setPlacePreview(state, place) {
      state.placePreview = place;
    },
    clearPlacePreview(state) {
      state.placePreview = null;
    },

    toggleReservationModal(state) {
      state.showReservationModal = !state.showReservationModal;
    },

    // updateSettings(state, settings) {
    //   const userData = storage.getItem('user');
    //   userData.user.settings = settings;
    //   storage.setItem('user', userData);
    //   state.user.user.settings = settings;
    // },

    clearUserData(state) {
      state.user = null;
      state.authChecked = true;
      onboardingStoreParts.mutations.clearOnboardingState(state);
    },

    message(state, message) {
      const rand = Math.floor(Math.floor(Math.random() * 10000));
      state.messages.push({
        id: rand,
        model: rand,
        text: message,
      });
    },

    clearErrors(state) {
      state.errorsBackend = {};
    },
    setErrors(state, errors) {
      state.errorsBackend = errors;
    },

    loading(state, loading) {
      state.loading = loading;
    },

    setSilverBackgroundColor(state) {
      state.backgroundColor = '#f2f2f2';
    },
    removeBackgroundColor(state) {
      state.backgroundColor = 'transparent';
    },
    ...onboardingStoreParts.mutations,
  },

  actions: {
    applyAuthenticatedUser({ commit, state }, user) {
      commit('updateUser', user);
      commit('setUserOnboarding', user.onboarding_flags);
      commit('setAuthChecked', true);

      if (user && user.settings && user.settings.lang) {
        i18n.locale = user.settings.lang;
        axios.defaults.headers.common['App-Language'] = i18n.locale;
      }
      if (user && window.cordova_iab) {
        window.cordova_iab.postMessage(JSON.stringify({ type: 'user-logged-in', user_id: user.id }));
      } else if (user && window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.cordova_iab) {
        window.webkit.messageHandlers.cordova_iab.postMessage(JSON.stringify({ type: 'user-logged-in', user_id: user.id }));
      }

      return state.user;
    },
    loadProfile({ dispatch, commit, state }, options = {}) {
      if (state.user && !options.force) {
        return state.user;
      }

      return axios.get('me')
        .then((res) => {
          dispatch('applyAuthenticatedUser', res.data);
          return res.data;
        })
        .catch((error) => {
          commit('clearUserData');
          if (error.response && error.response.status === 401) {
            return null;
          }
          throw error;
        });
    },
    checkAuth({ dispatch, state }) {
      if (state.authChecked) {
        return Promise.resolve(state.user);
      }

      return AuthApi.csrf()
        .then(() => dispatch('loadProfile', { force: true }));
    },
    updateUser({ commit }, user) {
      commit('updateUser', user);
      commit('setUserOnboarding', user.onboarding_flags);
    },
    loginForApp({ dispatch }, authData) {
      const urlParams = new URLSearchParams(window.location.search);
      const redirectUrl = urlParams.get('redirect_url') || null;

      return axios
        .post(`/sociallogin/${authData.provider}`, { ...authData, redirect_url: redirectUrl })
        .then(({ data: loginResponse }) => {
          const authParams = { ...loginResponse };
          dispatch('loginUser', authParams);
        })
        .catch((err) => {
          console.log({ err });
        });
    },
    tryLogin({ dispatch }, credentials) {
      const urlParams = new URLSearchParams(window.location.search);
      const redirectUrl = urlParams.get('redirect_url') || null;

      return AuthApi
        .login({ ...credentials, redirect_url: redirectUrl })
        .then(({ data: loginResponse }) => dispatch('loginUser', loginResponse));
    },
    loginUser({ commit, dispatch }, loginResponse = {}) {
      commit('clearErrors');
      if (loginResponse.user) {
        dispatch('applyAuthenticatedUser', loginResponse.user);
      }

      const urlParams = new URLSearchParams(window.location.search);
      const redirectUrl = urlParams.get('redirect_url') || '/user-settings/account';
      if (loginResponse.delay) {
        setTimeout(() => {
          window.location = redirectUrl;
        }, loginResponse.delay);
      } else {
        window.location = redirectUrl;
      }
    },
    logout({ commit }) {
      if (window.cordova_iab) {
        window.cordova_iab.postMessage(JSON.stringify({ type: 'user-logged-out' }));
      } else if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.cordova_iab) {
        window.webkit.messageHandlers.cordova_iab.postMessage(JSON.stringify({ type: 'user-logged-out' }));
      }
      return AuthApi.logout()
        .catch(() => null)
        .finally(() => {
          commit('clearUserData');
        });
    },

    message({ commit }, message) {
      commit('message', message);
    },
    setErrors({ commit }, errors) {
      commit('setErrors', errors);
    },
    clearErrors({ commit }) {
      commit('clearErrors');
    },

    updateSettings(store, settings) {
      // commit('updateSettings', settings);
      return axios
        .post('user-settings/update-settings', settings);
    },

    loading({ commit }, loading) {
      commit('loading', loading);
    },

    setSilverBackgroundColor({ commit }) {
      commit('setSilverBackgroundColor');
    },
    removeBackgroundColor({ commit }) {
      commit('removeBackgroundColor');
    },

    getAdCategories(store, withAppend = '') {
      return axios
        .get(`panel/ads/categories?with=${withAppend}`)
        .then(({ data }) => data);
    },
    getAdCategoryByConst(store, categoryConst) {
      return axios
        .get(`panel/ads/categories/const/${categoryConst}`)
        .then(({ data }) => data);
    },

    getAds() {
      return axios
        .get('panel/ads')
        .then(({ data }) => data);
    },
    getAdsForCategoryConst(store, payload) {
      const params = typeof payload === 'string'
        ? { categoryConst: payload }
        : payload;
      const { categoryConst, limit } = params;
      return axios
        .get(`panel/ads/category-const/${categoryConst}`, {
          params: {
            limit: limit || null,
          },
        })
        .then(({ data }) => data);
    },
    getAd(store, adId) {
      return axios
        .get(`panel/ads/${adId}`)
        .then(({ data }) => data);
    },
    getCategoryTags(store, categoryId) {
      return axios
        .get(`panel/ads/tags/category/${categoryId}`)
        .then(({ data }) => data);
    },
    getSubcategoriesForCategory(store, categoryId) {
      return axios
        .get(`panel/ads/subcategories/category/${categoryId}`)
        .then(({ data }) => data);
    },
    getCountries() {
      return axios
        .get('countries')
        .then(({ data }) => data);
    },
    getPlaceTypes() {
      return axios
        .get('panel/ads/places-types')
        .then(({ data }) => data);
    },
    getPlaceBusinessStatuses() {
      return axios
        .get('panel/ads/places-business-statuses')
        .then(({ data }) => data);
    },
    getPlaceTagFields() {
      return axios
        .get('panel/ads/places-tag-fields')
        .then(({ data }) => data);
    },
    getPlaceCurrencies() {
      return axios
        .get('panel/ads/places-currencies')
        .then(({ data }) => data);
    },
    specialPricing(store, id) {
      return axios
        .get(`calendar/special-price/${id}`)
        .then(({ data }) => data);
    },
    minStayBlocked(store, params) {
      return axios
        .get(`calendar/min-stay-blocked/${params.id}`, {
          params: { start: params.start },
        })
        .then(({ data }) => data);
    },
    calendar(store, params) {
      return axios
        .get(`calendar/${params.id}`, {
          params: {
            start: params.start,
            excludeReservationID: params.excludeReservationID ?? null,
          },
        })
        .then(({ data }) => data);
    },
    calendarInitData(store, id) {
      return axios
        .get(`calendar/init-data/${id}`)
        .then(({ data }) => data);
    },
    calendarRules(store, id) {
      return axios
        .get(`calendar/rules/${id}`)
        .then(({ data }) => data);
    },
    calculatePrice({ commit }, params) {
      return axios
        .get(`calendar/${params.id}/pricing`, {
          params,
        })
        // .then(({ data }) => data);
        .then(({ data }) => {
          commit('setPriceCalculator', data);
          return data;
        });
    },
    editReservationId({ commit }, params) {
      commit('editReservationId', params);
    },
    deleteReservation({ commit }, params) {
      commit('deleteReservation', params);
    },
    clearDeleteReservation({ commit }) {
      commit('clearDeleteReservation');
    },

    loadReservation(store, id) {
      return axios
        .get(`calendar/reservations/${id}`)
        .then(({ data }) => data);
    },
    setReservationPreview({ commit }, params) {
      return axios
        .get(`calendar/reservations/${params}`)
        .then(({ data }) => {
          commit('setReservationPreview', data);
          return data;
        });
    },
    setHotelReservationPreview({ commit }, params) {
      return axios
        .get(`hotels/reservations/${params}`)
        .then(({ data }) => {
          commit('setHotelReservationPreview', data);
          return data;
        });
    },
    setInquiryPreview({ commit }, params) {
      return axios
        .get(`calendar/inquries/${params}`)
        .then(({ data }) => {
          commit('setInquiryPreview', data);
          return data;
        });
    },
    clearReservationPreview({ commit }) {
      commit('clearReservationPreview');
    },
    clearHotelReservationPreview({ commit }) {
      commit('clearHotelReservationPreview');
    },
    clearInquiryPreview({ commit }) {
      commit('clearInquiryPreview');
    },
    refreshIcal(store, id) {
      return axios
        .post(`calendar/refresh-ical/${id}`)
        .then(({ data }) => data);
    },
    getPlaces(store, payload = {}) {
      const { limit } = payload;
      return axios
        .get('panel/ads/places', {
          params: {
            limit: limit || undefined,
          },
        })
        .then(({ data }) => data);
    },
    setPlacePreview({ commit }, id) {
      return axios
        .get(`panel/ads/places/${id}`)
        .then(({ data }) => {
          commit('setPlacePreview', data);
          return data;
        });
    },
    clearPlacePreview({ commit }) {
      commit('clearPlacePreview');
    },
    ...onboardingStoreParts.actions,
  },

  getters: {
    isLogged: (state) => !!state.user,
    authChecked: (state) => state.authChecked,
    user: (state) => state.user,
    isAdmin: (state) => state.user && state.user.rolesAndPermissions && state.user.rolesAndPermissions.roles && state.user.rolesAndPermissions.roles.includes('Admin'),
    messages: (state) => state.messages,
    loading: (state) => state.loading,
    backgroundColor: (state) => state.backgroundColor,
    errorsBackend: (state) => state.errorsBackend,
    calculator: (state) => state.calculator,
    editReservationId: (state) => state.editReservationId,
    deleteReservation: (state) => state.deleteReservation,
    reservationPreview: (state) => state.reservationPreview,
    hotelReservationPreview: (state) => state.hotelReservationPreview,
    inquiryPreview: (state) => state.inquiryPreview,
    placePreview: (state) => state.placePreview,
    showReservationModal: (state) => state.showReservationModal,
    hasValidationErrors: (state) => state.errorsBackend && Object.keys(state.errorsBackend).length,
    settings: (state) => {
      const defaultSettings = {
        lang: 'sr',
      };
      return { ...defaultSettings, ...(state.user && state.user.settings ? state.user.settings : {}) };
    },
    ...onboardingStoreParts.getters,
  },
});
